import { useQueryClient } from '@tanstack/react-query';
import { type ComponentPropsWithoutRef, memo, useCallback, useEffect } from 'react';

import { type FiltersTrackingLocation, useTrackFiltersApplied, useTrackFiltersRemoved } from '@air/analytics';
import { Filters } from '@air/next-icons';
import { cn } from '@air/utils-tailwind';

import { ColorFilterTrigger } from '~/components/Filters/FiltersBar/ColorFilterTrigger';
import { CreatedDateFilterTrigger } from '~/components/Filters/FiltersBar/CreatedDateFilterTrigger';
import { CustomFieldFilterTrigger } from '~/components/Filters/FiltersBar/CustomFieldFilterTrigger';
import { LocationsFilterTrigger } from '~/components/Filters/FiltersBar/LocationsFilterTrigger';
import { ModifiedDateFilterTrigger } from '~/components/Filters/FiltersBar/ModifiedDateFilterTrigger';
import { MoreFiltersTrigger } from '~/components/Filters/FiltersBar/MoreFiltersTrigger';
import { OrientationFilterTrigger } from '~/components/Filters/FiltersBar/OrientationFilterTrigger';
import { OtherFiltersTrigger } from '~/components/Filters/FiltersBar/OtherFiltersTrigger';
import { SmartTagFilterTrigger } from '~/components/Filters/FiltersBar/SmartTagFilterTrigger';
import { TagFilterTrigger } from '~/components/Filters/FiltersBar/TagFilterTrigger';
import { TypeFilterTrigger } from '~/components/Filters/FiltersBar/TypeFilterTrigger';
import { UploadedDateFilterTrigger } from '~/components/Filters/FiltersBar/UploadedDateFilterTrigger';
import { UploaderFilterTrigger } from '~/components/Filters/FiltersBar/UploaderFilterTrigger';
import { defaultOtherFilters } from '~/components/Filters/OtherFilters/OtherFilters';
import { type ItemTypeFilter, type SingleFilterProps } from '~/components/Filters/types';
import { usePrivateFastFilterOptions } from '~/components/Filters/v2/hooks/usePrivateFastFilterOptions';
import { manuallyHideDropdown } from '~/components/Menus/DropdownMenuWithActionSheet';
import { useApplyFilters } from '~/hooks/filters/useApplyFilters';
import { useFetchAnyWorkspacePermissions } from '~/hooks/useFetchAnyWorkspacePermissions';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { filtersValuesSelector } from '~/store/filters/selectors';
import { type SelectedFilter } from '~/store/filters/types';
import { filterTypeToTrackingType } from '~/store/filters/utils';
import {
  getPrivateFiltersOptionsKey,
  useGetPrivateFilterOptions,
} from '~/swr-hooks/filters/useGetPrivateFilterOptions';
import { filterParamsToTrackingType, getUrlParamsFromFilters } from '~/utils/filters/filtersToUrlParamsUtils';
import {
  canSeeCustomFields,
  canSeeWorkspaceSmartTags,
  canSeeWorkspaceTags,
} from '~/utils/permissions/workspacePermissions';
import { useAirStore } from '~/utils/ReduxUtils';

export type FiltersBarProps = Pick<ComponentPropsWithoutRef<'div'>, 'className'> &
  Pick<SingleFilterProps, 'onOpenChange'> & {
    availableFilterTypes?: ItemTypeFilter[];
    trackLocation: FiltersTrackingLocation;
  };

const DEFAULT_AVAILABLE_FILTER_TYPES: ItemTypeFilter[] = ['assets', 'files', 'boards'];

export const PrivateFiltersBar = memo(
  ({
    availableFilterTypes = DEFAULT_AVAILABLE_FILTER_TYPES,
    className,
    trackLocation,
    onOpenChange,
  }: FiltersBarProps) => {
    const {
      filtersInBar,
      filtersInDropdown,
      customFieldsInDropdown,
      otherFilters = defaultOtherFilters,
      disabledFilters,
    } = usePrivateFastFilterOptions();
    const { data: filtersOptions } = useGetPrivateFilterOptions();

    const { currentWorkspace } = useCurrentWorkspace();
    const queryClient = useQueryClient();

    useEffect(() => {
      queryClient.invalidateQueries({
        queryKey: getPrivateFiltersOptionsKey({ workspaceId: currentWorkspace?.id }),
      });
    }, [queryClient, currentWorkspace?.id]);

    const store = useAirStore();
    const { applyFilters, removeFilters } = useApplyFilters();
    const { trackFiltersRemoved } = useTrackFiltersRemoved();
    const { trackFiltersApplied } = useTrackFiltersApplied();
    const { data: permissions } = useFetchAnyWorkspacePermissions();
    const canViewCustomFields = canSeeCustomFields(permissions);
    const canSeeTags = canSeeWorkspaceTags(permissions);
    const canSeeSmartTags = canSeeWorkspaceSmartTags(permissions);

    const renderFilterTrigger = useCallback(
      (filter: SelectedFilter) => {
        const onApplyClick = async () => {
          const filters = filtersValuesSelector(store.getState());
          await applyFilters({ filters });
          manuallyHideDropdown();

          const appliedFilters = getUrlParamsFromFilters(filters);
          trackFiltersApplied({
            types: filterParamsToTrackingType(appliedFilters),
            location: trackLocation,
            builderType: 'pinned-filters',
          });
        };

        const onRemoveClick = async (filters: SelectedFilter[]) => {
          await removeFilters({ filters });
          trackFiltersRemoved({
            types: filterTypeToTrackingType(filter.type),
            location: trackLocation,
            builderType: 'pinned-filters',
          });
        };

        const commonProps: SingleFilterProps = {
          onApplyClick,
          onRemoveClick,
          onOpenChange,
        };

        switch (filter.type) {
          case 'tags':
            return canSeeTags && <TagFilterTrigger key={filter.type} {...commonProps} />;
          case 'smartTags':
            return canSeeSmartTags && <SmartTagFilterTrigger key={filter.type} {...commonProps} />;
          case 'color':
            return <ColorFilterTrigger key={filter.type} colors={filtersOptions?.colors ?? []} {...commonProps} />;
          case 'customField':
            return filter.customField && canViewCustomFields ? (
              <CustomFieldFilterTrigger key={filter.customField.id} customField={filter.customField} {...commonProps} />
            ) : null;
          case 'dateCreated':
            return <CreatedDateFilterTrigger key={filter.type} {...commonProps} />;
          case 'dateModified':
            return <ModifiedDateFilterTrigger key={filter.type} {...commonProps} />;
          case 'dateUploaded':
            return <UploadedDateFilterTrigger key={filter.type} {...commonProps} />;
          case 'other':
            return <OtherFiltersTrigger otherFilters={otherFilters} key={filter.type} {...commonProps} />;
          case 'type':
            return <TypeFilterTrigger key={filter.type} availableTypes={availableFilterTypes} {...commonProps} />;
          case 'uploader':
            return (
              <UploaderFilterTrigger uploaders={filtersOptions?.uploaders ?? []} key={filter.type} {...commonProps} />
            );
          case 'locations':
            return <LocationsFilterTrigger key={filter.type} {...commonProps} />;
          case 'orientation':
            return <OrientationFilterTrigger key={filter.type} {...commonProps} />;
          case 'separator1':
            return <div className="h-6 w-px shrink-0 bg-grey-4" aria-hidden="true" key={filter.type} />;
        }
      },
      [
        onOpenChange,
        store,
        applyFilters,
        trackFiltersApplied,
        trackLocation,
        removeFilters,
        trackFiltersRemoved,
        canSeeTags,
        canSeeSmartTags,
        filtersOptions?.colors,
        filtersOptions?.uploaders,
        canViewCustomFields,
        otherFilters,
        availableFilterTypes,
      ],
    );

    return (
      <div className={cn('flex flex-wrap items-center gap-2 overflow-auto px-3 pt-2 pb-3', className)}>
        <Filters className="size-4 shrink-0 text-grey-8" />
        {filtersInBar.map((filter) => renderFilterTrigger(filter))}
        <MoreFiltersTrigger
          trackLocation={trackLocation}
          otherFilters={otherFilters}
          availableTypes={availableFilterTypes}
          filters={filtersInDropdown}
          customFields={customFieldsInDropdown}
          disabledFilters={disabledFilters}
        />
      </div>
    );
  },
);

PrivateFiltersBar.displayName = 'PrivateFiltersBar';
